import type { Comment, Entity, Persisted } from '@tunasong/schemas'
import { isComment, isPersisted } from '@tunasong/schemas'

/**
 * Merge the two comment sources that feed the chat thread, deduplicating
 * by id across BOTH sources.
 *
 * - `incrementalEntities` — RTK Query children cache. Can contain both an
 *   optimistic copy (`addOptimistic` in `useChat.addComment`) and the
 *   server-synced version of the same comment during the brief window
 *   between create and refetch.
 * - `externalItems` — `state.entities.receivedEntities`, the server-pushed
 *   entity stream. Pushed comments are canonical post-confirm; for
 *   agent-authored comments they may arrive here AND via a children refetch.
 *
 * GH #1288: the old logic only deduplicated `externalItems` against
 * `incrementalEntities` (set lookup on the latter). Duplicates *within*
 * either source flowed through unchecked, causing React's "two children
 * with the same key" warning on the chat thread.
 *
 * Order matters: incremental first, external second — when the same id
 * appears in both, the externally-pushed (server-canonical) copy overrides.
 */
export const mergeChatComments = (
  incrementalEntities: readonly Persisted<Comment>[],
  externalItems: readonly Entity[],
  parentId: string | undefined
): Persisted<Comment>[] => {
  const byId = new Map<string, Persisted<Comment>>()

  for (const c of incrementalEntities) {
    byId.set(c.id, c)
  }

  if (parentId) {
    for (const e of externalItems) {
      if (e.parentId !== parentId) {
        continue
      }
      if (!isComment(e) || !isPersisted(e)) {
        continue
      }
      byId.set(e.id, e)
    }
  }

  return Array.from(byId.values())
}
